import React, { PureComponent } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import {
  withStyles,
  Toolbar,
  Typography,
  TextField,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@material-ui/core";
import {
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Grid,
  Tooltip,
  IconButton,
  Popover,
  Radio,
  RadioGroup,
  FormControlLabel,
  Chip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Divider,
  Checkbox,
  Box,
} from "@mui/material";
import {
  Download,
  CalendarMonth as CalendarMonthIcon,
  Visibility as VisibilityIcon,
  Person as PersonIcon,
  Business as BusinessIcon,
  CheckCircleOutline as CheckCircleOutlineIcon,
  HighlightOff as HighlightOffIcon,
  Close as CloseIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  Send as SendIcon,
  Unsubscribe as UnsubscribeIcon,
} from "@mui/icons-material";
import debounce from "lodash/debounce";
import dayjs from "dayjs";

import SectionPaper from "../../common/SectionPaper";
import BrandedLoading from "../../common/BrandedLoading";
import EmptyMessage from "../../common/EmptyMessage";
import Pagination from "../../common/Pagination";
import { checkPermission } from "../../common/Sidebar/checkPermission";
import { messageShow } from "../../common/api/actions";
import { getPlatformUsers, exportPlatformUsers } from "../api/actions/query";
import { getTemplates } from "../../CRM/api/actions/query";
import TemplateSelectionPopup from "../../CRM/List/Messaging";
import routes from "../api/routes";
import styles from "./styles";

const formatDate = (iso) => {
  if (!iso) return "—";
  try {
    return dayjs(iso).format("DD-MM-YYYY hh:mm A");
  } catch {
    return iso;
  }
};

const getInitials = (name) =>
  (name || "NA")
    .split(" ")
    .slice(0, 2)
    .map((n) => n[0])
    .join("")
    .toUpperCase();

class List extends PureComponent {
  constructor(props) {
    super(props);
    const initialPage = props.match?.params?.page ? parseInt(props.match.params.page, 10) || 1 : 1;

    this.state = {
      isLoading: false,
      users: [],
      count: 0,
      page: initialPage,
      searchField: "all",
      searchQuery: "",
      createdAtFilter: "all",
      yearFilter: "",
      monthFilter: "",
      calendarAnchorEl: null,
      selectedUser: null,
      openDetailModal: false,
      editModalOpen: false,
      editFormData: {
        _id: "",
        name: "",
        email: "",
        mobile: "",
        role: "candidate",
        isVerified: false,
        gender: "",
        dateOfBirth: "",
      },
      templatePopupOpen: false,
      selectedUserId: null,
      templates: [],
      permissions: {
        canView: false,
        canCreate: false,
        canEdit: false,
        canDelete: false,
      },
      isSuperAdmin: false,
    };

    this.fetchUsersDebounced = debounce(this.fetchUsers.bind(this), 200);
  }

  componentDidMount() {
    const { match } = this.props;
    const page = parseInt(match?.params?.page, 10) || this.state.page;
    this.verifyPermissions();
    this.fetchUsers(page);
    this.fetchTemplates();
  }

  UNSAFE_componentWillReceiveProps(nextProps) {
    const { match } = nextProps;
    if (match?.params?.page && match.params.page !== this.props.match?.params?.page) {
      const page = parseInt(match.params.page, 10) || 1;
      this.setState({ page }, () => {
        this.fetchUsers(page);
      });
    }
  }

  verifyPermissions = async () => {
    try {
      const userRole = localStorage.getItem("user");
      if (userRole) {
        const userData = JSON.parse(userRole);
        const isSuperAdmin = userData?.roleDetails?.name === "Super Admin";
        this.setState({ isSuperAdmin });
      }

      const permissions = await checkPermission("engagement", "users");
      if (typeof permissions === "object" && permissions !== null) {
        this.setState({ permissions });
      } else if (permissions === true) {
        this.setState({
          permissions: { canView: true, canEdit: true, canCreate: true, canDelete: true },
        });
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
    }
  };

  fetchTemplates = async () => {
    try {
      const response = await getTemplates();
      if (response && response.data?.data) {
        this.setState({ templates: response.data.data });
      }
    } catch (error) {
      console.error("Failed to fetch templates:", error);
    }
  };

  fetchUsers = async (page = this.state.page) => {
    const { getPlatformUsers, messageShow } = this.props;
    const { searchField, searchQuery, createdAtFilter, yearFilter, monthFilter } = this.state;

    this.setState({ isLoading: true });

    try {
      const response = await getPlatformUsers({
        page,
        limit: 20,
        searchQuery,
        searchField,
        createdAt: createdAtFilter,
        year: yearFilter,
        month: monthFilter,
      });

      if (response && response.success && response.data) {
        const users = response.data.users || response.data.list || [];
        const count = response.data.count || response.data.pagination?.total || users.length;
        this.setState({
          users,
          count,
          page,
          isLoading: false,
        });
      } else {
        this.setState({ users: [], count: 0, isLoading: false });
        if (response && response.message) {
          messageShow(response.message);
        }
      }
    } catch (error) {
      console.error("Error fetching users:", error);
      this.setState({ users: [], count: 0, isLoading: false });
      messageShow("Failed to load users. Please try again.");
    }
  };

  handleSearchFieldChange = (event) => {
    const selectedField = event.target.value;
    if (this.state.searchQuery.trim() !== "") {
      this.setState({ searchField: selectedField, searchQuery: "", page: 1 }, () => {
        this.fetchUsers(1);
      });
    } else {
      this.setState({ searchField: selectedField, page: 1 });
    }
  };

  handleSearchQueryChange = (event) => {
    const searchQuery = event.target.value;
    this.setState({ searchQuery }, () => {
      if (searchQuery.trim() === "") {
        this.setState({ page: 1 }, () => {
          this.fetchUsers(1);
        });
      }
    });
  };

  handleSearch = () => {
    this.setState({ page: 1 }, () => {
      this.fetchUsers(1);
    });
  };

  handleCalendarClick = (event) => {
    this.setState({ calendarAnchorEl: event.currentTarget });
  };

  handleCalendarClose = () => {
    this.setState({ calendarAnchorEl: null });
  };

  handleYearChange = (year) => {
    this.setState({ yearFilter: year, page: 1 }, () => {
      this.fetchUsers(1);
    });
  };

  handleMonthChange = (month) => {
    this.setState({ monthFilter: month, page: 1 }, () => {
      this.fetchUsers(1);
    });
  };

  handleFilterClick = (filter) => {
    const { createdAtFilter } = this.state;
    this.setState(
      {
        createdAtFilter: createdAtFilter === filter ? "all" : filter,
        page: 1,
      },
      () => {
        this.fetchUsers(1);
      }
    );
  };

  handlePageChange = (page) => {
    this.setState({ page }, () => {
      if (this.props.history) {
        this.props.history.push(routes.engagementUsers.path(page));
      }
      this.fetchUsers(page);
    });
  };

  handleDownload = async () => {
    const { exportPlatformUsers, messageShow } = this.props;
    const { searchField, searchQuery, createdAtFilter, yearFilter, monthFilter } = this.state;

    messageShow("Preparing users export...");

    try {
      const response = await exportPlatformUsers({
        searchQuery,
        searchField,
        createdAt: createdAtFilter,
        year: yearFilter,
        month: monthFilter,
      });

      if (response && response.success && response.data?.users) {
        const users = response.data.users;
        if (users.length === 0) {
          messageShow("No users to export.");
          return;
        }

        const headers = ["Name", "Email", "Mobile", "Role", "Status", "Joined Date"];
        const rows = users.map((u) => [
          `"${(u.name || "").replace(/"/g, '""')}"`,
          `"${(u.email || "").replace(/"/g, '""')}"`,
          `"${(u.mobile || "").replace(/"/g, '""')}"`,
          `"${(u.role || "").replace(/"/g, '""')}"`,
          `"${u.isVerified ? "Verified" : "Unverified"}"`,
          `"${formatDate(u.createdAt)}"`,
        ]);

        const csvContent =
          "data:text/csv;charset=utf-8," +
          [headers.join(","), ...rows.map((e) => e.join(","))].join("\n");

        const encodedUri = encodeURI(csvContent);
        const link = document.createElement("a");
        link.setAttribute("href", encodedUri);
        link.setAttribute(
          "download",
          `Users-Collection-${dayjs().format("YYYY-MM-DD-HH-mm")}.csv`
        );
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);

        messageShow("Users downloaded successfully.");
      } else {
        messageShow(response?.message || "Failed to export users.");
      }
    } catch (error) {
      console.error("Error downloading users:", error);
      messageShow("Error downloading users.");
    }
  };

  handleOpenDetail = (user) => {
    this.setState({ selectedUser: user, openDetailModal: true });
  };

  handleCloseDetail = () => {
    this.setState({ selectedUser: null, openDetailModal: false });
  };

  // Edit User Handlers
  handleEditClick = (user) => {
    this.setState({
      editFormData: {
        _id: user._id,
        name: user.name || "",
        email: user.email || "",
        mobile: user.mobile || "",
        role: user.role || "candidate",
        isVerified: Boolean(user.isVerified),
        gender: user.gender || "",
        dateOfBirth: user.dateOfBirth || "",
      },
      editModalOpen: true,
    });
  };

  handleEditFormChange = (e) => {
    const { name, value, type, checked } = e.target;
    this.setState((prev) => ({
      editFormData: {
        ...prev.editFormData,
        [name]: type === "checkbox" ? checked : value,
      },
    }));
  };

  handleCloseEditModal = () => {
    this.setState({ editModalOpen: false });
  };

  // Send Template / Email Handlers
  handleSendClick = (userId) => {
    this.setState({ selectedUserId: userId, templatePopupOpen: true });
  };

  handleCloseTemplatePopup = () => {
    this.setState({ templatePopupOpen: false, selectedUserId: null });
  };

  renderRoleBadge = (role) => {
    const { classes } = this.props;
    const r = (role || "").toLowerCase();
    let badgeClass = classes.defaultBadge;

    if (r === "candidate") {
      badgeClass = classes.candidateBadge;
    } else if (r === "company") {
      badgeClass = classes.companyBadge;
    }

    const label = role ? role.charAt(0).toUpperCase() + role.slice(1) : "Unknown";

    return (
      <Chip
        label={label}
        size="small"
        className={badgeClass}
        variant="outlined"
      />
    );
  };

  render() {
    const { classes } = this.props;
    const {
      isLoading,
      users,
      count,
      searchField,
      searchQuery,
      createdAtFilter,
      yearFilter,
      monthFilter,
      calendarAnchorEl,
      selectedUser,
      openDetailModal,
      editModalOpen,
      editFormData,
      templatePopupOpen,
      selectedUserId,
      templates,
      permissions,
      isSuperAdmin,
    } = this.state;

    const open = Boolean(calendarAnchorEl);
    const id = open ? "calendar-popover" : undefined;
    const currentYear = new Date().getFullYear();
    const years = Array.from(new Array(5), (val, index) => currentYear - index);

    const months = [
      { value: "", label: "Months" },
      { value: "01", label: "January" },
      { value: "02", label: "February" },
      { value: "03", label: "March" },
      { value: "04", label: "April" },
      { value: "05", label: "May" },
      { value: "06", label: "June" },
      { value: "07", label: "July" },
      { value: "08", label: "August" },
      { value: "09", label: "September" },
      { value: "10", label: "October" },
      { value: "11", label: "November" },
      { value: "12", label: "December" },
    ];

    const hasViewAccess = isSuperAdmin || permissions.canView;
    const isInputDisabled = !searchField || searchField === "all";

    return (
      <div>
        {/* Header Title Toolbar */}
        <Toolbar className={classes.toolbar}>
          <Typography variant="h6" color="inherit" className={classes.grow}>
            Users
          </Typography>
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Tooltip title="View Email Unsubscribed List (Users)">
              <Button
                variant="outlined"
                size="small"
                onClick={() => this.props.history.push("/users/unsubscribed")}
                startIcon={<UnsubscribeIcon sx={{ color: "#dc2626" }} />}
                sx={{
                  borderColor: "#fca5a5",
                  color: "#b91c1c",
                  bgcolor: "#fff5f5",
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: "0.85rem",
                  borderRadius: "8px",
                  px: 1.75,
                  py: 0.6,
                  "&:hover": {
                    bgcolor: "#fee2e2",
                    borderColor: "#f87171",
                  },
                }}
              >
                Email Unsubscribed List
              </Button>
            </Tooltip>
          </Box>
        </Toolbar>

        {/* Search and Download Toolbar */}
        <Toolbar className={classes.toolbar}>
          <div className={classes.searchFieldsAll}>
            <Grid container spacing={2} alignItems="center">
              <Grid item>
                <FormControl variant="outlined" className={classes.select}>
                  <InputLabel id="search-field-label">Search By</InputLabel>
                  <Select
                    labelId="search-field-label"
                    id="search-field-select"
                    value={searchField}
                    onChange={this.handleSearchFieldChange}
                    label="Search By"
                    style={{
                      width: "130px",
                      height: "50px",
                      fontSize: "1rem",
                    }}
                  >
                    <MenuItem value="all">All</MenuItem>
                    <MenuItem value="name">Name</MenuItem>
                    <MenuItem value="email">Email</MenuItem>
                    <MenuItem value="mobile">Mobile</MenuItem>
                    <MenuItem value="role">Role</MenuItem>
                  </Select>
                </FormControl>
              </Grid>
              <Grid item>
                <TextField
                  placeholder="Search..."
                  value={searchQuery}
                  onChange={this.handleSearchQueryChange}
                  className={classes.searchInput}
                  disabled={isInputDisabled}
                />
              </Grid>
              <Grid item>
                <Button
                  variant="outlined"
                  color="primary"
                  onClick={this.handleSearch}
                  disabled={isInputDisabled || !searchQuery.trim()}
                  style={{
                    borderRadius: "5px",
                    textTransform: "none",
                    padding: "5px 15px",
                    fontSize: "0.9rem",
                  }}
                >
                  Search
                </Button>
              </Grid>
            </Grid>
            <div>
              <Tooltip
                title="Download Users"
                arrow
                disableHoverListener={permissions.canEdit || isSuperAdmin ? false : true}
              >
                <span>
                  <Button
                    color="primary"
                    startIcon={<Download />}
                    size="large"
                    onClick={this.handleDownload}
                    className={classes.downloadButton}
                    disabled={!permissions.canEdit && !isSuperAdmin}
                  >
                    Download
                  </Button>
                </span>
              </Tooltip>
            </div>
          </div>
        </Toolbar>

        {/* Joined By Date Filter Toolbar */}
        <Toolbar className={classes.toolbar}>
          <div className={classes.joinedSearchAll}>
            <Typography variant="h6" color="inherit" className={classes.label}>
              Joined By
            </Typography>
            <div>
              <IconButton onClick={this.handleCalendarClick} size="medium">
                <CalendarMonthIcon />
              </IconButton>
              <Popover
                id={id}
                open={open}
                anchorEl={calendarAnchorEl}
                onClose={this.handleCalendarClose}
                anchorOrigin={{
                  vertical: "bottom",
                  horizontal: "center",
                }}
                transformOrigin={{
                  vertical: "top",
                  horizontal: "center",
                }}
              >
                <div className={classes.calendarPopover}>
                  <Select
                    value={yearFilter || ""}
                    onChange={(event) => this.handleYearChange(event.target.value)}
                    displayEmpty
                    className={classes.select}
                  >
                    <MenuItem value="" className={classes.menuItem}>
                      <em>Years</em>
                    </MenuItem>
                    {years.map((year) => (
                      <MenuItem key={year} value={year}>
                        {year}
                      </MenuItem>
                    ))}
                  </Select>
                  <Select
                    value={monthFilter || ""}
                    onChange={(event) => this.handleMonthChange(event.target.value)}
                    displayEmpty
                    className={classes.select}
                  >
                    {months.map((month) => (
                      <MenuItem key={month.value} value={month.value}>
                        {month.label}
                      </MenuItem>
                    ))}
                  </Select>
                </div>
              </Popover>
            </div>
            <div>
              <FormControl component="fieldset">
                <RadioGroup
                  row
                  value={createdAtFilter}
                  onChange={(event) => this.handleFilterClick(event.target.value)}
                  sx={{ display: "flex", width: "350px" }}
                >
                  <FormControlLabel
                    value="new"
                    control={<Radio size="small" />}
                    label="New(<30days)"
                    sx={{ "& .MuiTypography-root": { fontSize: "1rem" } }}
                  />
                  <FormControlLabel
                    value="old"
                    control={<Radio size="small" />}
                    label="Old(>30days)"
                    sx={{ "& .MuiTypography-root": { fontSize: "1rem" } }}
                  />
                  <FormControlLabel
                    value="all"
                    control={<Radio size="small" />}
                    label="All"
                    sx={{ "& .MuiTypography-root": { fontSize: "1rem" } }}
                  />
                </RadioGroup>
              </FormControl>
            </div>
          </div>
        </Toolbar>

        {/* Wrap table alone with SectionPaper */}
        <SectionPaper>
          {isLoading ? (
            <BrandedLoading />
          ) : !hasViewAccess ? (
            <div style={{ padding: 24, textAlign: "center" }}>
              <Typography color="error" variant="body1">
                You do not have permission to view this section.
              </Typography>
            </div>
          ) : users.length === 0 ? (
            <div style={{ padding: 24 }}>
              <EmptyMessage message="No users to show." />
            </div>
          ) : (
            <>
              <div className={classes.tableWrapper}>
                <Table>
                  <TableHead>
                    <TableRow>
                      <TableCell className={classes.tableHeaderCell}>Name</TableCell>
                      <TableCell className={classes.tableHeaderCell}>Email</TableCell>
                      <TableCell className={classes.tableHeaderCell}>Mobile</TableCell>
                      <TableCell className={classes.tableHeaderCell}>Role</TableCell>
                      <TableCell className={classes.tableHeaderCell}>Status</TableCell>
                      <TableCell className={classes.tableHeaderCell}>Joined Date</TableCell>
                      <TableCell className={classes.tableHeaderCell} align="center">
                        Actions
                      </TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {users.map((user) => (
                      <TableRow key={user._id} hover>
                        <TableCell>
                          <Typography variant="body2">{user.name || "N/A"}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                            <Typography variant="body2">{user.email || "—"}</Typography>
                            {user.emailSubscription === false && (
                              <Chip
                                label="UNSUBSCRIBED"
                                size="small"
                                sx={{
                                  bgcolor: "#fee2e2",
                                  color: "#dc2626",
                                  fontWeight: 700,
                                  fontSize: "0.68rem",
                                  height: 20,
                                  borderRadius: "4px",
                                }}
                              />
                            )}
                          </Box>
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2">{user.mobile || "—"}</Typography>
                        </TableCell>
                        <TableCell>{this.renderRoleBadge(user.role)}</TableCell>
                        <TableCell>
                          {user.isVerified ? (
                            <Chip
                              icon={<CheckCircleOutlineIcon style={{ fontSize: 14 }} />}
                              label="Verified"
                              size="small"
                              style={{
                                backgroundColor: "#f0fdf4",
                                color: "#16a34a",
                                border: "1px solid #bbf7d0",
                              }}
                            />
                          ) : (
                            <Chip
                              icon={<HighlightOffIcon style={{ fontSize: 14 }} />}
                              label="Unverified"
                              size="small"
                              style={{
                                backgroundColor: "#fef2f2",
                                color: "#dc2626",
                                border: "1px solid #fecaca",
                              }}
                            />
                          )}
                        </TableCell>
                        <TableCell>
                          <Typography variant="body2" color="textSecondary">
                            {formatDate(user.createdAt)}
                          </Typography>
                        </TableCell>
                        <TableCell align="center">
                          <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
                            {/* Send Email / Template */}
                            <Tooltip title="Send Email" arrow>
                              <span>
                                <Button
                                  color="primary"
                                  size="small"
                                  onClick={() => this.handleSendClick(user._id)}
                                  disabled={!permissions.canEdit && !isSuperAdmin}
                                  style={{ minWidth: 36, padding: 6 }}
                                >
                                  <SendIcon fontSize="small" />
                                </Button>
                              </span>
                            </Tooltip>
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>

              {/* Global Shared Pagination Component */}
              <Pagination
                count={count}
                route={routes.engagementUsers}
                onPageChange={this.handlePageChange}
              />
            </>
          )}
        </SectionPaper>

        {/* View User Detail Modal */}
        {selectedUser && (
          <Dialog
            open={openDetailModal}
            onClose={this.handleCloseDetail}
            maxWidth="sm"
            fullWidth
          >
            <DialogTitle>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "space-between",
                }}
              >
                <Typography variant="h6">User Details</Typography>
                <IconButton size="small" onClick={this.handleCloseDetail}>
                  <CloseIcon fontSize="small" />
                </IconButton>
              </div>
            </DialogTitle>
            <Divider />
            <DialogContent style={{ paddingTop: 20 }}>
              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 15,
                  marginBottom: 20,
                }}
              >
                <div
                  style={{
                    width: 48,
                    height: 48,
                    borderRadius: "50%",
                    backgroundColor: "#0d3559",
                    color: "#ffffff",
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontWeight: 700,
                    fontSize: "16px",
                  }}
                >
                  {getInitials(selectedUser.name)}
                </div>
                <div>
                  <Typography variant="h6" style={{ fontWeight: 600 }}>
                    {selectedUser.name}
                  </Typography>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginTop: 4 }}>
                    {this.renderRoleBadge(selectedUser.role)}
                    {selectedUser.isVerified ? (
                      <Chip
                        label="Verified"
                        size="small"
                        style={{ backgroundColor: "#f0fdf4", color: "#16a34a" }}
                      />
                    ) : (
                      <Chip
                        label="Unverified"
                        size="small"
                        style={{ backgroundColor: "#fef2f2", color: "#dc2626" }}
                      />
                    )}
                  </div>
                </div>
              </div>

              <Grid container spacing={2}>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                    Email
                  </Typography>
                  <Typography variant="body2">{selectedUser.email || "—"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                    Mobile
                  </Typography>
                  <Typography variant="body2">{selectedUser.mobile || "—"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                    Gender
                  </Typography>
                  <Typography variant="body2">{selectedUser.gender || "—"}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                    Date of Birth
                  </Typography>
                  <Typography variant="body2">
                    {selectedUser.dateOfBirth ? formatDate(selectedUser.dateOfBirth) : "—"}
                  </Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                    Joined Platform
                  </Typography>
                  <Typography variant="body2">{formatDate(selectedUser.createdAt)}</Typography>
                </Grid>
                <Grid item xs={6}>
                  <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                    Last Updated
                  </Typography>
                  <Typography variant="body2">{formatDate(selectedUser.updatedAt)}</Typography>
                </Grid>
                {selectedUser.roleDetails && (
                  <Grid item xs={12}>
                    <Typography variant="caption" color="textSecondary" style={{ fontWeight: 600 }}>
                      Assigned Custom Role
                    </Typography>
                    <Typography variant="body2">
                      {selectedUser.roleDetails.name || selectedUser.roleAssigned}
                    </Typography>
                  </Grid>
                )}
              </Grid>
            </DialogContent>
            <Divider />
            <DialogActions>
              <Button onClick={this.handleCloseDetail} variant="contained" color="primary">
                Close
              </Button>
            </DialogActions>
          </Dialog>
        )}

        {/* Template Selection Popup (Send Email) */}
        <TemplateSelectionPopup
          open={templatePopupOpen}
          onClose={this.handleCloseTemplatePopup}
          contactId={selectedUserId}
          templates={templates.filter((t) => !t.isDefault && t.type !== "CRM")}
          type={"STORE"}
        />
      </div>
    );
  }
}

List.propTypes = {
  classes: PropTypes.object.isRequired,
  getPlatformUsers: PropTypes.func.isRequired,
  exportPlatformUsers: PropTypes.func.isRequired,
  messageShow: PropTypes.func.isRequired,
  history: PropTypes.object,
  match: PropTypes.object,
};

export default connect(null, {
  getPlatformUsers,
  exportPlatformUsers,
  messageShow,
})(withStyles(styles)(List));
